import React, { useState, useEffect, useCallback } from "react";
import { Settings, Save, LayoutDashboard, FileText, Users, UserCheck, Shield, AlertCircle, Lock, CheckCircle, Bell, BrainCircuit } from "lucide-react";
import { usePermissions } from "shared/context/PermissionsContext";
import { CURRENT_WBR_WEEK } from "./AdminConstants";
import { Toast, TabButton } from "./AdminShared";
import FunctionDetailsTab from "./FunctionDetailsTab";
import MeetingRolesTab from "./MeetingRolesTab";
import SidebarConfigTab from "./SidebarConfigTab";
import POCManagementTab from "./POCManagementTab";
import AccessManagementTab from "./AccessManagementTab";
import NotificationsTab from "./NotificationsTab";
import AiPromptsTab from "./AiPromptsTab";
import { getAuthHeaders, handleResponse } from "shared/api/function_data";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateConfig } from "shared/lib/queryClient";
import "./AdminPage.css";

const BASE_URL = "https://1ftxw98ih3.execute-api.eu-west-1.amazonaws.com/cia_pulse_1";

const TABS = [
  { id: "details", label: "Function Details", icon: LayoutDashboard },
  { id: "meeting", label: "Meeting Roles", icon: Users },
  { id: "sidebar", label: "Sidebar Config", icon: FileText },
  { id: "poc", label: "POC Management", icon: UserCheck },
  { id: "access", label: "Access", icon: Shield },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "ai", label: "AI Insights", icon: BrainCircuit },
];

// ─── API helpers (isolated per-section saves) ───

async function saveSection(functionId, year, weeks, sectionKey, sectionData) {
  const body = {
    function_id: functionId,
    year: year,
    weeks: weeks,
    [sectionKey]: sectionData,
  };

  const response = await fetch(`${BASE_URL}/update_global_config`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  handleResponse(response);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to save ${sectionKey}`);
  }
  return response.json();
}

// ─── Week Target Selector (simplified) ───

function WeekTargetSelector({ weeks, setWeeks, updateMode, setUpdateMode, year }) {
  const weekList = Array.from({ length: 52 }, (_, i) => i + 1);

  return (
    <div className="admin-target-selector">
      <div className="admin-target-selector__header">
        <AlertCircle size={16} />
        <h3>Save to Week(s)</h3>
      </div>
      <div className="admin-target-selector__body">
        <div className="admin-target-selector__row">
          <div className="admin-field">
            <label className="admin-field__label">Mode</label>
            <select
              className="admin-input admin-select"
              value={updateMode}
              onChange={(e) => {
                setUpdateMode(e.target.value);
                if (e.target.value === "all") setWeeks("all");
                else if (e.target.value === "single") setWeeks([CURRENT_WBR_WEEK]);
                else setWeeks([CURRENT_WBR_WEEK]);
              }}
            >
              <option value="single">Current Week Only</option>
              <option value="range">Week Range</option>
              <option value="all">All Weeks ({year})</option>
            </select>
          </div>

          {updateMode === "single" && (
            <div className="admin-field">
              <label className="admin-field__label">Week</label>
              <select
                className="admin-input admin-select"
                value={Array.isArray(weeks) ? weeks[0] : CURRENT_WBR_WEEK}
                onChange={(e) => setWeeks([Number(e.target.value)])}
              >
                {weekList.map((w) => (
                  <option key={w} value={w}>Week {w}{w === CURRENT_WBR_WEEK ? " (current)" : ""}</option>
                ))}
              </select>
            </div>
          )}

          {updateMode === "range" && (
            <>
              <div className="admin-field">
                <label className="admin-field__label">From</label>
                <select
                  className="admin-input admin-select"
                  value={Array.isArray(weeks) ? weeks[0] : 1}
                  onChange={(e) => {
                    const start = Number(e.target.value);
                    const end = Array.isArray(weeks) ? weeks[weeks.length - 1] : 52;
                    const range = Array.from({ length: Math.max(1, end - start + 1) }, (_, i) => start + i);
                    setWeeks(range);
                  }}
                >
                  {weekList.map((w) => <option key={w} value={w}>Week {w}</option>)}
                </select>
              </div>
              <div className="admin-field">
                <label className="admin-field__label">To</label>
                <select
                  className="admin-input admin-select"
                  value={Array.isArray(weeks) ? weeks[weeks.length - 1] : 52}
                  onChange={(e) => {
                    const start = Array.isArray(weeks) ? weeks[0] : 1;
                    const end = Number(e.target.value);
                    const range = Array.from({ length: Math.max(1, end - start + 1) }, (_, i) => start + i);
                    setWeeks(range);
                  }}
                >
                  {weekList.filter((w) => w >= (Array.isArray(weeks) ? weeks[0] : 1)).map((w) => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {updateMode === "all" && (
          <div className="admin-target-selector__info">
            <AlertCircle size={14} />
            <span>Changes apply to all 52 weeks in {year}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
//  MAIN ADMIN PAGE V2
// ═══════════════════════════════════════════

function AdminPageV2({ theme, functionId, week, year, functionConfig: parentConfig }) {
  const { canAdmin, role } = usePermissions();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("details");
  const [toast, setToast] = useState(null);

  // Per-section local state (independent of each other)
  const [details, setDetails] = useState(null);
  const [sidebar, setSidebar] = useState(null);
  const [poc, setPoc] = useState(null);

  // Original copies for dirty-checking
  const [origDetails, setOrigDetails] = useState(null);
  const [origSidebar, setOrigSidebar] = useState(null);
  const [origPoc, setOrigPoc] = useState(null);

  // Save state per section
  const [savingSection, setSavingSection] = useState(null);
  const [savedSection, setSavedSection] = useState(null);

  // Week targeting (for function_details, sidebar, poc saves)
  const [updateMode, setUpdateMode] = useState("single");
  const [targetWeeks, setTargetWeeks] = useState([week || CURRENT_WBR_WEEK]);

  // Load config
  useEffect(() => {
    if (parentConfig) {
      const d = JSON.parse(JSON.stringify(parentConfig.function_details || {}));
      const s = JSON.parse(JSON.stringify(parentConfig.function_sidebar || {}));
      const p = JSON.parse(JSON.stringify(parentConfig.function_poc || {}));
      setDetails(d);
      setSidebar(s);
      setPoc(p);
      setOrigDetails(JSON.parse(JSON.stringify(d)));
      setOrigSidebar(JSON.parse(JSON.stringify(s)));
      setOrigPoc(JSON.parse(JSON.stringify(p)));
    }
  }, [parentConfig]);

  // Dirty checks
  const detailsDirty = details && origDetails && JSON.stringify(details) !== JSON.stringify(origDetails);
  const sidebarDirty = sidebar && origSidebar && JSON.stringify(sidebar) !== JSON.stringify(origSidebar);
  const pocDirty = poc && origPoc && JSON.stringify(poc) !== JSON.stringify(origPoc);

  const currentTabDirty = (() => {
    if (activeTab === "details") return detailsDirty;
    if (activeTab === "sidebar") return sidebarDirty;
    if (activeTab === "poc") return pocDirty;
    return false;
  })();

  // ─── Save handler (only saves the active tab's section) ───
  const handleSave = async () => {
    const sectionMap = {
      details: { key: "function_details", data: details, setOrig: setOrigDetails },
      sidebar: { key: "function_sidebar", data: sidebar, setOrig: setOrigSidebar },
      poc: { key: "function_poc", data: poc, setOrig: setOrigPoc },
    };

    const section = sectionMap[activeTab];
    if (!section) return;

    setSavingSection(activeTab);
    try {
      await saveSection(functionId, year, targetWeeks, section.key, section.data);
      // A config save can touch any week (single/range/all), so refresh the
      // whole config domain + meeting roster for this function.
      invalidateConfig(queryClient, functionId);
      section.setOrig(JSON.parse(JSON.stringify(section.data)));
      setSavedSection(activeTab);
      setToast({ message: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} saved!`, type: "success" });
      setTimeout(() => setSavedSection(null), 2000);
    } catch (err) {
      setToast({ message: `Save failed: ${err.message}`, type: "error" });
    } finally {
      setSavingSection(null);
    }
  };

  // ─── Discard handler ───
  const handleDiscard = () => {
    if (activeTab === "details") setDetails(JSON.parse(JSON.stringify(origDetails)));
    if (activeTab === "sidebar") setSidebar(JSON.parse(JSON.stringify(origSidebar)));
    if (activeTab === "poc") setPoc(JSON.parse(JSON.stringify(origPoc)));
    setToast({ message: "Changes discarded", type: "success" });
  };

  // ─── Config wrapper for child components (they expect `config` with setConfig) ───
  const configForDetails = details ? { function_id: functionId, function_details: details } : null;
  const setConfigForDetails = useCallback((updater) => {
    setDetails((prev) => {
      const full = { function_id: functionId, function_details: prev };
      const updated = typeof updater === "function" ? updater(full) : updater;
      return updated.function_details;
    });
  }, [functionId]);

  const configForSidebar = sidebar ? { function_id: functionId, function_sidebar: sidebar } : null;
  const setConfigForSidebar = useCallback((updater) => {
    setSidebar((prev) => {
      const full = { function_id: functionId, function_sidebar: prev };
      const updated = typeof updater === "function" ? updater(full) : updater;
      return updated.function_sidebar;
    });
  }, [functionId]);

  const configForPoc = poc ? { function_id: functionId, function_poc: poc } : null;
  const setConfigForPoc = useCallback((updater) => {
    setPoc((prev) => {
      const full = { function_id: functionId, function_poc: prev };
      const updated = typeof updater === "function" ? updater(full) : updater;
      return updated.function_poc;
    });
  }, [functionId]);

  // ─── Access denied ───
  if (!canAdmin) {
    return (
      <div className="admin-panel-embedded">
        <div className="admin-access-denied">
          <Lock size={48} />
          <h2>Admin Access Required</h2>
          <p>Your role is <strong>{role}</strong>. Only admins can access this panel.</p>
        </div>
      </div>
    );
  }

  if (!details || !sidebar || !poc) {
    return <div className="admin-loading"><div className="admin-loading__spinner" /><p>Loading...</p></div>;
  }

  const showWeekSelector = activeTab !== "access" && activeTab !== "meeting" && activeTab !== "notifications" && activeTab !== "ai";

  return (
    <div className="admin-panel-embedded">
      {/* ─── Header ─── */}
      <div className="admin-embedded-header">
        <div className="admin-embedded-header__left">
          <Settings size={20} />
          <div>
            <h2 className="admin-embedded-header__title">Admin Configuration</h2>
            <span className="admin-embedded-header__subtitle">
              {details?.label || functionId} · Week {week}, {year}
            </span>
          </div>
        </div>
        <div className="admin-embedded-header__right">
          {currentTabDirty && (
            <button className="admin-btn admin-btn--outline" onClick={handleDiscard}>
              Discard
            </button>
          )}
          {showWeekSelector && (
            <button
              className={`admin-btn admin-btn--primary ${savingSection === activeTab ? "admin-btn--loading" : ""}`}
              onClick={handleSave}
              disabled={!currentTabDirty || savingSection}
            >
              {savingSection === activeTab ? (
                <><span className="admin-btn__spinner" /> Saving...</>
              ) : savedSection === activeTab ? (
                <><CheckCircle size={14} /> Saved!</>
              ) : (
                <><Save size={14} /> Save {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ─── Dirty banner ─── */}
      {currentTabDirty && (
        <div className="admin-changes-banner">
          <AlertCircle size={14} />
          <span>Unsaved changes in {activeTab} — only {activeTab} file will be updated</span>
        </div>
      )}

      <div className="admin-body">
        {/* ─── Week Target (only for details/sidebar/poc) ─── */}
        {showWeekSelector && (
          <WeekTargetSelector
            weeks={targetWeeks}
            setWeeks={setTargetWeeks}
            updateMode={updateMode}
            setUpdateMode={setUpdateMode}
            year={year}
          />
        )}

        {/* ─── Tabs ─── */}
        <div className="admin-tabs">
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              icon={tab.icon}
              label={tab.label}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>

        {/* ─── Tab Content ─── */}
        <div className="admin-tab-panel">
          {activeTab === "details" && configForDetails && (
            <FunctionDetailsTab config={configForDetails} setConfig={setConfigForDetails} />
          )}
          {activeTab === "meeting" && (
            <MeetingRolesTab
              config={{ function_id: functionId, function_details: details }}
              setConfig={setConfigForDetails}
              functionId={functionId}
              year={year}
            />
          )}
          {activeTab === "sidebar" && configForSidebar && (
            <SidebarConfigTab config={configForSidebar} setConfig={setConfigForSidebar} />
          )}
          {activeTab === "poc" && configForPoc && (
            <POCManagementTab config={configForPoc} setConfig={setConfigForPoc} />
          )}
          {activeTab === "access" && (
            <AccessManagementTab functionId={functionId} functionSidebar={sidebar} year={year} />
          )}
          {activeTab === "notifications" && (
            <NotificationsTab functionId={functionId} year={year} />
          )}
          {activeTab === "ai" && (
            <AiPromptsTab functionId={functionId} year={year} functionSidebar={sidebar} />
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default AdminPageV2;
